import {Alert, Dialog} from 'platform/components';
import {Box, Grid, GridItem, Heading, Show, Text, VStack} from 'platform/foundation';

import i18n from '@dms/i18n';

import {isNilOrZero, suffixTestId, TestIdProps} from 'shared/web';

interface SourcingCarvagoBuyDialogProps extends TestIdProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  isConfirmLoading?: boolean;
  skippedInactiveCount?: number;
}

export function SourcingCarvagoBuyDialog(props: SourcingCarvagoBuyDialogProps) {
  return (
    <Dialog
      isOpen={props.isOpen}
      onClose={props.onClose}
      title={i18n.t('page.sourcing.labels.carvagoBuyDialogTitle')}
      buttons={[
        {
          title: i18n.t('general.actions.cancel'),
          onClick: props.onClose,
          variant: 'secondary',
          isDisabled: props.isConfirmLoading,
          'data-testid': suffixTestId('carvagoBuyDialog-cancel', props),
        },
        {
          title: i18n.t('general.actions.confirm'),
          onClick: props.onConfirm,
          variant: 'primary',
          isLoading: props.isConfirmLoading,
          'data-testid': suffixTestId('carvagoBuyDialog-confirm', props),
        },
      ]}
      data-testid={suffixTestId('carvagoBuyDialog', props)}
    >
      <VStack spacing={4}>
        <VStack spacing={1}>
          <Heading size={3}>{i18n.t('page.sourcing.labels.carvagoBuyDialogHeading')}</Heading>

          <Text size="small" color="secondary">
            {i18n.t('page.sourcing.labels.carvagoBuyDialogDescription')}
          </Text>
        </VStack>

        <Show when={!isNilOrZero(props.skippedInactiveCount)}>
          <Alert
            variant="warning"
            message={i18n.t('page.sourcing.labels.carvagoBuyDialogSkippedSoldWarning', {
              count: props.skippedInactiveCount ?? 0,
            })}
            data-testid={suffixTestId('carvagoBuyDialog-skippedSoldWarning', props)}
          />
        </Show>

        <Box
          backgroundColor="palettes.neutral.20.100"
          borderColor="palettes.neutral.40.100"
          border="1px solid"
          borderRadius="small"
          padding={4}
        >
          <VStack spacing={4}>
            <Text size="base" alternative>
              {i18n.t('page.sourcing.labels.carvagoBuyDialogChooseServiceTitle')}
            </Text>

            <Grid columns={3} spacing={4}>
              <GridItem>
                <VStack spacing={1}>
                  <Text size="xSmall" color="tertiary">
                    {i18n.t('page.sourcing.labels.carvagoBuyDialogStep', {count: 1})}
                  </Text>
                  <Text size="base" alternative>
                    {i18n.t('page.sourcing.labels.carvagoBuyDialogStep1Title')}
                  </Text>
                  <Text size="xSmall" color="secondary">
                    {i18n.t('page.sourcing.labels.carvagoBuyDialogStep1Description')}
                  </Text>
                </VStack>
              </GridItem>
              <GridItem>
                <VStack spacing={1}>
                  <Text size="xSmall" color="tertiary">
                    {i18n.t('page.sourcing.labels.carvagoBuyDialogStep', {count: 2})}
                  </Text>
                  <Text size="base" alternative>
                    {i18n.t('page.sourcing.labels.carvagoBuyDialogStep2Title')}
                  </Text>
                  <Text size="xSmall" color="secondary">
                    {i18n.t('page.sourcing.labels.carvagoBuyDialogStep2Description')}
                  </Text>
                </VStack>
              </GridItem>
              <GridItem>
                <VStack spacing={1}>
                  <Text size="xSmall" color="tertiary">
                    {i18n.t('page.sourcing.labels.carvagoBuyDialogStep', {count: 3})}
                  </Text>
                  <Text size="base" alternative>
                    {i18n.t('page.sourcing.labels.carvagoBuyDialogStep3Title')}
                  </Text>
                  <Text size="xSmall" color="secondary">
                    {i18n.t('page.sourcing.labels.carvagoBuyDialogStep3Description')}
                  </Text>
                </VStack>
              </GridItem>
            </Grid>
          </VStack>
        </Box>
      </VStack>
    </Dialog>
  );
}
